import { convertToStartOfWeekUTC, DateFormatType, formatDate } from "~/utils/datetime"
import { WEEK_MS } from "~/utils/demandProjection"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"

/**
 * NOOS plan-period rules (docs/helix/NOOS_PLAN_PERIOD.md): every baseline covers the same 52 plan
 * weeks, and a variant active for only part of them is SOURCED over that many weeks, never scaled.
 * Shared by the engine (which weeks plan) and the Assortment tab (which weeks it says were read).
 */
export const PLAN_PERIOD_WEEKS = 52

/** Plan-week indices, 0-based from the period start. `count` 0 = no active week in this plan. */
export type ActiveWeeks = {
  first: number
  /** Inclusive. */
  last: number
  count: number
  /** The start date falls after the period start, so it limits the window. */
  startsIn: boolean
  /** The end date (`activatedUntil`) falls before the period end, so it limits the window. */
  endsIn: boolean
}

const FULL_PERIOD: ActiveWeeks = {
  first: 0,
  last: PLAN_PERIOD_WEEKS - 1,
  count: PLAN_PERIOD_WEEKS,
  startsIn: false,
  endsIn: false,
}

/**
 * Whole weeks from `from`'s week to `date`'s, negative when `date` is earlier. Rounded, so every
 * week offset the engine takes (activation, season start, phase-out) agrees to the week.
 */
export const weekIndex = (from: Date, date: Date): number =>
  Math.round(
    (convertToStartOfWeekUTC(date).getTime() - convertToStartOfWeekUTC(from).getTime()) / WEEK_MS,
  )

/**
 * From the later of the period start and `start` to the earlier of the period end and `end`. The
 * variant is active in `end`'s week itself: it reads Phased out from the week after.
 */
export function activeWeeksOf(
  periodStart: Date,
  start: Date | null | undefined,
  end: Date | null | undefined,
  weeks: number = PLAN_PERIOD_WEEKS,
): ActiveWeeks {
  if (!start && !end && weeks === PLAN_PERIOD_WEEKS) return FULL_PERIOD
  const startIdx = start ? weekIndex(periodStart, start) : 0
  const endIdx = end ? weekIndex(periodStart, end) : weeks - 1
  const first = Math.max(0, startIdx)
  const last = Math.min(weeks - 1, endIdx)
  return {
    first,
    last,
    count: Math.max(0, last - first + 1),
    startsIn: startIdx > 0,
    endsIn: endIdx < weeks - 1,
  }
}

/** The same limits with no week left in them: ended, or not yet dated, so it plans nothing. */
export const withoutWeeks = (active: ActiveWeeks): ActiveWeeks => ({
  ...active,
  first: 0,
  last: -1,
  count: 0,
})

/**
 * Zero every week outside the active ones. With `level`, the kept weeks are rescaled to sum to it:
 * the level was already sourced over exactly these weeks, so this only places it.
 */
export function clipToActiveWeeks(shape: number[], active: ActiveWeeks, level?: number): number[] {
  if (active.first === 0 && active.last >= shape.length - 1) return shape
  const clipped = shape.map((v, i) => (i < active.first || i > active.last ? 0 : v))
  if (level == null) return clipped
  const sum = clipped.reduce((a, b) => a + b, 0)
  if (sum > 0) return clipped.map((v) => (v * level) / sum)
  // No curve left in the window: spread flat over it rather than lose a sourced level.
  if (active.count === 0) return clipped
  return clipped.map((_, i) => (i < active.first || i > active.last ? 0 : level / active.count))
}

/** What a period baseline is read from (NOOS_PLAN_PERIOD.md Step 1). */
export type PeriodBaselineSource =
  | "ownSales"
  | "ownSalesUnobserved"
  | "matches"
  | "plannerSet"
  | "none"

/**
 * How the source window relates to a full year (Step 2). `initial`: the matches' first `count`
 * weeks; `minusTail`: the last 52 weeks less the `removedWeeks` after the end date;
 * `lifeWeeks`: Introduced `soldWeeks` ago, its matches' `matchWeeks` life weeks from there, then
 * its own sales a year on over the weeks it sold;
 * `clipped`: a level whose weeks after the end are dropped without a source read of their own.
 */
export type PeriodWindowKind = "full" | "initial" | "minusTail" | "lifeWeeks" | "clipped" | "none"

/**
 * A New NOOS variant's baseline in its two parts (NOOS_PLAN_PERIOD.md "Placement"): its own sales a
 * year on over the `ownWeeks` plan weeks it sold a year earlier, and its matches over the
 * `matchWeeks` it didn't. The source the planner picks decides only the second part.
 */
export type NewVariantSplit = { ownWeeks: number; own: number; matchWeeks: number; matches: number }

/** One variant's plan-period facts, as the engine resolved them. Dates as UTC week-start ISO. */
export type PlanPeriod = {
  source: PeriodBaselineSource
  window: PeriodWindowKind
  activeWeeks: number
  /** Set when the start date limits the active weeks. */
  startWeek: string | null
  /** Set when the end date limits the active weeks. */
  endWeek: string | null
  /** `minusTail` only. */
  removedWeeks: number
  /** `lifeWeeks` only: the life week it is in, then how many of its matches' weeks are read. */
  soldWeeks: number
  matchWeeks: number
  /** `lifeWeeks` only: its own sales and its matches as planned (Normalized scales the matches). */
  plannedSplit: NewVariantSplit | null
  /** The source over a full 52-week window; null when the window is the whole period. */
  fullYear: number | null
  /** The variant's start (activation date, else first sale) and end, as the engine read them. ISO. */
  start: string | null
  startIsFirstSale: boolean
  activatedUntil: string | null
}

export const PERIOD_SOURCE_LABEL: Record<PeriodBaselineSource, string> = {
  ownSales: "Own sales",
  ownSalesUnobserved: "Own sales + unobserved",
  matches: "Matches",
  plannerSet: "Planner-set",
  none: "No basis",
}

const weekLabel = (iso: string) => formatDate(new Date(iso), DateFormatType.Default)

/** "32 of 52 weeks", with the date that limits them. */
export function activeWeeksLabel(p: PlanPeriod): string {
  const count = `${formatNumber(p.activeWeeks)} of ${PLAN_PERIOD_WEEKS} weeks`
  const limits = [
    p.startWeek ? `from ${weekLabel(p.startWeek)}` : null,
    p.endWeek ? `until ${weekLabel(p.endWeek)}` : null,
  ].filter(Boolean)
  return limits.length > 0 ? `${count}, ${limits.join(" ")}` : count
}

/** "Matches' weeks 28 to 52 after launch, then 27 weeks of own sales": a New variant's two parts. */
const lifeWeeksLabel = (p: PlanPeriod): string => {
  const matches = `Matches' weeks ${formatNumber(p.soldWeeks + 1)} to ${formatNumber(p.soldWeeks + p.matchWeeks)} after launch`
  const ownWeeks = p.plannedSplit?.ownWeeks ?? 0
  if (ownWeeks <= 0) return matches
  return `${matches}, then ${formatNumber(ownWeeks)} ${ownWeeks === 1 ? "week" : "weeks"} of own ${formatName("sale", true)}`
}

/** Which weeks of the source were summed. */
export function sourceWindowLabel(p: PlanPeriod): string {
  const weeks = (n: number) => `${formatNumber(n)} ${n === 1 ? "week" : "weeks"}`
  if (p.source === "none" && p.window !== "none") return "Nothing to read"
  switch (p.window) {
    case "none":
      return "No weeks in this plan"
    case "initial":
      return p.source === "plannerSet"
        ? `Planner's units for ${weeks(p.activeWeeks)}`
        : `Matches' first ${weeks(p.activeWeeks)}`
    case "minusTail":
      return `Last 52 weeks, minus ${weeks(p.removedWeeks)} after the end date`
    case "lifeWeeks":
      return lifeWeeksLabel(p)
    case "clipped":
      return `Its year, up to the end date`
    case "full":
      if (p.source === "matches" || p.source === "plannerSet") return "A full first year"
      return "Last 52 weeks"
  }
}
